<template>
    <listings-find
        :params="{ listing: $route.params.listing }"
        :include="[
            'property,files(filters:with_erased=false)'
        ]"
        :preloaded="preloaded('listing')"
        :find-on-created="!preloaded('listing')"
        @success="onSuccess"
    >
        <div
            slot-scope="{
                has_loaded,
                is_loading,
                data: listing,
                error,
                find
            }"
             class="container max-w-3xl mx-auto"
        >
            <template v-if="!has_loaded">
                <base-loading
                    :retry="find"
                    :error="error"
                    class="py-6"
                />

                <error-modal
                    v-if="error && $error.is(error, { exclude: [400, 422] })"
                    :error="error"
                />
            </template>

            <template v-else>
                <grid-layout class="mb-24">
                    <base-alert
                        v-if="!listing.is_active"
                        level="warning"
                        class="mx-3 mb-6"
                    >
                        <template slot="heading">Draft Listing</template>

                        <template slot="content">
                            <p>This listing is a draft and not yet active (it is not visible to the public). Activate this listing from your dashboard.</p>
                        </template>
                    </base-alert>

                    <grid-layout-item class="mb-6">
                        <image-carousel :images="carouselImages(listing)" />
                    </grid-layout-item>

                    <grid-layout-item class="mb-6 md:w-7/12 lg:w-8/12 md:mb-0">
                        <div class="pb-3 border-b-2 rounded rounded-l-none border-grey-lightest">
                            <div class="mb-4 text-xs font-bold xs:text-sm text-label text-grey-dark">
                                {{ listing.number_of_bedrooms }} Bed {{ listing.property_type }} -

                                <span class="xs:hidden">
                                    Avl.
                                </span>
                                <span class="hidden xs:inline">
                                    Available
                                </span>

                                <template v-if="listing.available_at && $moment(listing.available_at).diff($moment()) > 0">
                                    {{ listing.available_at | moment('D MMM YYYY') }}
                                </template>
                                <template v-else>
                                    Now
                                </template>
                            </div>

                            <div class="mb-0 text-4xl font-semibold leading-tight md:text-5xl text-grey-darkest">
                                {{ listing.rent_amount | money(0) }}<!--
                                --><span class="text-sm md:text-base text-grey-dark"> / month</span>
                            </div>

                            <div class="mb-4 text-sm font-semibold md:text-base text-grey-dark">
                                + {{ listing.deposit_amount | money(0) }} deposit
                            </div>

                            <base-address
                                :address="listing.property.address"
                                :is-inline="true"
                                class="text-sm font-semibold sm:text-base text-grey-darker"
                            />
                        </div>

                        <div class="flex justify-between p-4 text-xs md:justify-start text-grey-darker sm:text-sm">
                            <div class="inline-flex items-center md:mr-8">
                                <base-svg
                                    name="icon-sofa"
                                    class="w-5 mr-2 sm:w-6"
                                />

                                <span class="font-bold text-grey-darkest">{{ listing.number_of_receptions }}</span>
                                &nbsp;
                                <span class="xs:hidden">
                                    Rcpt.
                                </span>
                                <span class="hidden xs:inline">
                                    Receptions
                                </span>
                            </div>

                            <div class="inline-flex items-center md:mr-8">
                                <base-svg
                                    name="icon-bed"
                                    class="w-5 mr-2 sm:w-6"
                                />

                                <span class="font-bold text-grey-darkest">{{ listing.number_of_bedrooms }}</span>
                                &nbsp;
                                <span class="xs:hidden">
                                    Bdrm.
                                </span>
                                <span class="hidden xs:inline">
                                    Bedrooms
                                </span>
                            </div>

                            <div class="inline-flex items-center">
                                <base-svg
                                    name="icon-bath"
                                    class="w-5 mr-2 sm:w-6"
                                />

                                <span class="font-bold text-grey-darkest">{{ listing.number_of_bathrooms }}</span>
                                &nbsp;
                                <span class="xs:hidden">
                                    Bthr.
                                </span>
                                <span class="hidden xs:inline">
                                    Bathrooms
                                </span>
                            </div>
                        </div>

                        <div class="px-4 py-2 mb-6 text-xs leading-loose rounded bg-grey-lightest text-label text-grey-darker sm:text-sm">
                            <grid-layout>
                                <grid-layout-item class="w-1/2 py-1">
                                    <div class="flex items-center">
                                        <base-svg
                                            :name="listing.is_furnished ? 'icon-check-circle' : 'icon-close-circle'"
                                            :class="listing.is_furnished ? 'fill-primary-green fill-secondary-green-lighter' : 'fill-primary-red fill-secondary-red-lighter'"
                                            class="flex-none w-6 h-6 mr-2"
                                        />

                                        Furnished
                                    </div>
                                </grid-layout-item>

                                <grid-layout-item class="w-1/2 py-1">
                                    <div class="flex items-center">
                                        <base-svg
                                            :name="listing.allows_pets ? 'icon-check-circle' : 'icon-close-circle'"
                                            :class="listing.allows_pets ? 'fill-primary-green fill-secondary-green-lighter' : 'fill-primary-red fill-secondary-red-lighter'"
                                            class="flex-none w-6 h-6 mr-2"
                                        />

                                        Pets
                                    </div>
                                </grid-layout-item>

                                <grid-layout-item class="w-1/2 py-1">
                                    <div class="flex items-center">
                                        <base-svg
                                            :name="listing.has_parking ? 'icon-check-circle' : 'icon-close-circle'"
                                            :class="listing.has_parking ? 'fill-primary-green fill-secondary-green-lighter' : 'fill-primary-red fill-secondary-red-lighter'"
                                            class="flex-none w-6 h-6 mr-2"
                                        />

                                        Parking
                                    </div>
                                </grid-layout-item>

                                <grid-layout-item class="w-1/2 py-1">
                                    <div class="flex items-center">
                                        <base-svg
                                            :name="listing.allows_dss ? 'icon-check-circle' : 'icon-close-circle'"
                                            :class="listing.allows_dss ? 'fill-primary-green fill-secondary-green-lighter' : 'fill-primary-red fill-secondary-red-lighter'"
                                            class="flex-none w-6 h-6 mr-2"
                                        />

                                        DSS
                                    </div>
                                </grid-layout-item>

                                <grid-layout-item class="w-1/2 py-1">
                                    <div class="flex items-center">
                                        <base-svg
                                            :name="listing.has_garden ? 'icon-check-circle' : 'icon-close-circle'"
                                            :class="listing.has_garden ? 'fill-primary-green fill-secondary-green-lighter' : 'fill-primary-red fill-secondary-red-lighter'"
                                            class="flex-none w-6 h-6 mr-2"
                                        />

                                        Garden
                                    </div>
                                </grid-layout-item>

                                <grid-layout-item class="w-1/2 py-1">
                                    <div class="flex items-center">
                                        <base-svg
                                            :name="listing.allows_smoking ? 'icon-check-circle' : 'icon-close-circle'"
                                            :class="listing.allows_smoking ? 'fill-primary-green fill-secondary-green-lighter' : 'fill-primary-red fill-secondary-red-lighter'"
                                            class="flex-none w-6 h-6 mr-2"
                                        />

                                        Smoking
                                    </div>
                                </grid-layout-item>
                            </grid-layout>
                        </div>

                        <div
                            v-if="listing.video_url"
                            class="mb-8"
                        >
                            <h2 class="mb-4 text-xl font-semibold text-grey-darkest">Video Tour</h2>

                            <div class="relative mb-6 aspect-ratio-16/9">
                                <youtube-player
                                    class="absolute inset-0 border"
                                    :video-id="getYouTubeId(listing.video_url)"
                                    player-width="100%"
                                    player-height="100%"
                                    :player-vars="{
                                        autoplay: 0,
                                        showinfo: 0,
                                        controls: 0,
                                        autohide: 1
                                    }"
                                />
                            </div>
                        </div>

                        <div class="mb-8 text-grey-darker">
                            <h2 class="mb-4 text-xl font-semibold text-grey-darkest">Description</h2>

                            <base-expandable>
                                <template v-slot:short>
                                    <div class="whitespace-pre-wrap">{{ listing.description | limit(300) }}</div>
                                </template>

                                <template v-slot:long>
                                    <div class="whitespace-pre-wrap">{{ listing.description }}</div>
                                </template>

                                <template v-slot:trigger="{ isOpen: is_open, toggle }">
                                    <button
                                        class="inline-flex items-center mt-1 text-blue focus:outline-none"
                                        @click.prevent="toggle"
                                    >
                                        Read {{ is_open ? 'less' : 'more' }}

                                        <base-svg
                                            :name="`icon-cheveron-${is_open ? 'up' : 'down'}`"
                                            :key="`icon-cheveron-${is_open ? 'up' : 'down'}`"
                                            class="w-5 h-5 fill-primary-blue"
                                        />
                                    </button>
                                </template>
                            </base-expandable>
                        </div>

                        <div class="md:mb-8 text-grey-darker">
                            <h2 class="mb-4 text-xl font-semibold text-grey-darkest">Location</h2>

                            <a
                                :href="`https://www.google.com/maps/search/?api=1&query=${listing.property.address.latitude},${listing.property.address.longitude}`"
                                class="no-underline text-blue"
                                target="_blank"
                            >
                                <base-address
                                    :address="listing.property.address"
                                    :is-inline="true"
                                    class="mb-3"
                                />

                                <base-map
                                    v-if="listing.property.address.latitude && listing.property.address.latitude"
                                    :latitude="listing.property.address.latitude"
                                    :longitude="listing.property.address.longitude"
                                    :zoom="15"
                                    :width="750"
                                    :height="250"
                                    class="w-full rounded"
                                />
                            </a>
                        </div>

                        <rentora-ad class="hidden md:block" />
                    </grid-layout-item>

                    <grid-layout-item class="md:w-5/12 lg:w-4/12 lg:pl-6">
                        <div class="mb-1 text-center text-label">
                            Share this property
                        </div>

                        <div class="flex justify-center mb-6">
                            <a
                                :href="`https://www.facebook.com/sharer/sharer.php?u=${listing.links.www.show}`"
                                class="block px-2"
                                rel="noopener"
                                target="_blank"
                            >
                                <base-svg
                                    name="icon-facebook-circle"
                                    class="w-12 h-12 fill-primary-blue-facebook fill-secondary-white"
                                />
                            </a>

                            <a
                                :href="`https://twitter.com/intent/tweet?url=${listing.links.www.show}&text=&via=rentorauk`"
                                class="block px-2"
                                rel="noopener"
                                target="_blank"
                            >
                                <base-svg
                                    name="icon-twitter-circle"
                                    class="w-12 h-12 fill-primary-blue-twitter fill-secondary-white"
                                />
                            </a>

                            <a
                                :href="`mailto:?subject=${title}%20-%20Rentora&body=${listing.links.www.show}`"
                                class="block px-2"
                                rel="noopener"
                                target="_blank"
                            >
                                <base-svg
                                    name="icon-mail-circle"
                                    class="w-12 h-12 fill-primary-blue fill-secondary-white"
                                />
                            </a>
                        </div>

                        <div class="mb-6 bg-white rounded shadow">
                            <div class="w-full rounded-full rounded-b-none bg-blue h-1-5" />

                            <div class="px-6 pt-6">
                                <header class="flex items-center mb-4">
                                    <base-svg
                                        name="icon-chat"
                                        class="w-6 h-6 mr-3 fill-secondary-blue fill-primary-blue-lighter"
                                    />

                                    <h6 class="text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Got a question?
                                    </h6>
                                </header>

                                <div class="mb-6 text-grey-darker">
                                    <p>Send a message directly to the landlord/agent of this property.</p>
                                </div>

                                <laroute-link
                                    :to="{
                                        name: 'listings.messages.create',
                                        params: {
                                            listing: listing.uuid
                                        }
                                    }"
                                    class="w-full mb-6 btn btn-blue"
                                >
                                    Message
                                </laroute-link>
                            </div>
                        </div>

                        <div class="mb-6 bg-white rounded shadow">
                            <div class="w-full rounded-full rounded-b-none bg-green h-1-5" />

                            <div class="px-6 pt-6">
                                <header class="flex items-center mb-4">
                                    <base-svg
                                        name="icon-inbox"
                                        class="w-6 h-6 mr-3 fill-secondary-green fill-primary-green-light"
                                    />

                                    <h6 class="text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Submit an application
                                    </h6>
                                </header>

                                <div class="mb-6 text-grey-darker">
                                    <p>Want to live here? Apply online now. Applications are sent directly to the landlord/agent of this property.</p>
                                </div>

                                <laroute-link
                                    :to="{
                                        name: 'listings.applications.store',
                                        params: {
                                            listing: listing.uuid
                                        }
                                    }"
                                    class="w-full mb-6 btn btn-green"
                                >
                                    Apply
                                </laroute-link>
                            </div>
                        </div>

                        <rentora-ad class="md:hidden" />

                        <div class="mb-6 text-grey-darker">
                            <h2 class="mb-4 text-xl font-semibold text-grey-darkest">Too good to be true?</h2>

                            <p class="mb-3">Learn how to protect yourself from rental scams.</p>

                            <a
                                href="https://help.rentora.co.uk/article/39-how-can-i-tell-if-a-listing-is-legitimate"
                                class="inline-flex items-center text-blue"
                                target="_blank"
                                rel="noopener"
                            >
                                How can I tell if a listing is legitimate?

                                <base-svg
                                    name="icon-external-window"
                                    class="w-4 h-4 ml-1 fill-primary-blue fill-secondary-blue"
                                />
                            </a>
                        </div>

                        <div class="mb-6 text-grey-darker">
                            <h2 class="mb-4 text-xl font-semibold text-grey-darkest">Report this listing</h2>

                            <p class="mb-3">Learn how to protect yourself from rental scams.</p>

                            <a
                                href="https://help.rentora.co.uk/contact"
                                class="inline-flex items-center text-blue"
                                target="_blank"
                                rel="noopener"
                            >
                                Contact us

                                <base-svg
                                    name="icon-external-window"
                                    class="w-4 h-4 ml-1 fill-primary-blue fill-secondary-blue"
                                />
                            </a>
                        </div>
                    </grid-layout-item>
                </grid-layout>

                <div class="fixed inset-x-0 bottom-0 flex p-3 m-3 bg-white border-t rounded shadow-lg bg-blue-bg-darker md:hidden">
                    <laroute-link
                        :to="{
                            name: 'listings.messages.create',
                            params: {
                                listing: listing.uuid
                            }
                        }"
                        class="w-full mr-3 btn btn-blue"
                    >
                        Message
                    </laroute-link>

                    <laroute-link
                        :to="{
                            name: 'listings.applications.store',
                            params: {
                                listing: listing.uuid
                            }
                        }"
                        class="w-full btn btn-green"
                    >
                        Apply
                    </laroute-link>
                </div>
            </template>
        </div>
    </listings-find>
</template>

<script>
    import _ from 'lodash'
    import HeadMixin from '../../mixins/head'
    import PreloadedMixin from '../../mixins/preloaded'
    import RentoraAd from './show/rentora-ad'
    import ListingsFind from '../../components/listings-find'

    const url = path => (_.preload('app.url') || '') + path

    export default {
        mixins: [
            HeadMixin,
            PreloadedMixin
        ],
        components: {
            RentoraAd,
            ListingsFind
        },
        data () {
            return {
                store: {
                    listing: _.get(this.preloaded('listing'), 'data')
                }
            }
        },
        computed: {
            title () {
                return this.store.listing
                    ? `${this.store.listing.number_of_bedrooms} bed ${this.store.listing.property_type} to rent in ${this.store.listing.property.address.town}`
                    : '...'
            }
        },
        methods: {
            carouselImages (listing) {
                const images = listing.files.data.map(file => {
                    return {
                        url: this.$laroute.route('storage.files.show', {
                            file: file.uuid,
                            width: 1200
                        }),
                        can_zoom: true
                    }
                })

                images.splice(2, 0, {
                    url: '/img/listings-marketing-2.jpg',
                    can_zoom: false
                })

                return images
            },
            onSuccess ({ data: listing }) {
                if (!this.$isSsr) {
                    this.store.listing = listing
                }
            },
            getYouTubeId (url) {
                var regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
                var match = url.match(regExp)

                if (match && match[2].length === 11) {
                    return match[2]
                }
            },
            head () {
                return {
                    title: [
                        this.title
                    ],
                    description: 'Pay your rent online, build your credit history, find your perfect home, view inventories and more.',
                    meta: [
                        {
                            property: 'og:image',
                            content: url(this.$laroute.route('listings.image', {
                                listing: this.$route.params.listing,
                                width: 1200,
                                height: 630
                            }))
                        },
                        {
                            name: 'twitter:image',
                            content: url(this.$laroute.route('listings.image', {
                                listing: this.$route.params.listing,
                                width: 1012,
                                height: 506
                            }))
                        }
                    ]
                }
            }
        }
    }
</script>
